// pages/ResetPasswordPage.tsx
import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Alert,
    InputAdornment,
    IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff, LockReset as LockResetIcon } from '@mui/icons-material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { request } from '../../../../utils/request';

const ResetPasswordPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');
    const userId = searchParams.get('id');
    
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [validating, setValidating] = useState(true);
    const [isValidToken, setIsValidToken] = useState(false);

    // Validate token on component mount
    useEffect(() => {
        const validateToken = async () => {
            if (!token || !userId) {
                setError('Invalid reset link');
                setValidating(false);
                return;
            }

            try {
                const response = await request({
                    url: `auth/validate-reset-token?token=${token}&id=${userId}`,
                    method: 'get',
                });
                
                if (response.status === 'success') {
                    setIsValidToken(true);
                } else {
                    setError('Invalid or expired reset link');
                }
            } catch (err: any) {
                setError(err?.message || 'Invalid or expired reset link');
            } finally {
                setValidating(false);
            }
        };

        validateToken();
    }, [token, userId]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!newPassword || !confirmPassword) {
            setError('Please fill in all fields');
            return;
        }

        if (newPassword.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await request({
                url: 'auth/reset-password',
                method: 'post',
                data: {
                    id: userId,
                    token: token,
                    newPassword: newPassword,
                },
            });

            if (response.status === 'success') {
                setSuccess(true);
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            } else {
                setError(response.message || 'Failed to reset password');
            }
        } catch (err: any) {
            setError(err?.message || 'Failed to reset password');
        } finally {
            setLoading(false);
        }
    };

    if (validating) {
        return (
            <Box sx={{ 
                minHeight: '100vh', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            }}>
                <CircularProgress />
                <Typography sx={{ ml: 2, color: 'white' }}>Validating reset link...</Typography>
            </Box>
        );
    }

    if (!isValidToken) {
        return (
            <Box sx={{ 
                minHeight: '100vh', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                // background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                p: 2
            }}>
                <Paper elevation={0} sx={{ maxWidth: 450, width: '100%', p: 4, borderRadius: 1, textAlign: 'center' }}>
                    <LockResetIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
                    <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
                        Invalid Reset Link
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {error || 'This password reset link is invalid or has expired.'}
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => navigate('/forgot-password')}
                        sx={{ textTransform: 'none' }}
                    >
                        Request New Link
                    </Button>
                </Paper>
            </Box>
        );
    }

    if (success) {
        return (
            <Box sx={{ 
                minHeight: '100vh', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                // background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                p: 2
            }}>
                <Paper elevation={0} sx={{ maxWidth: 450, width: '100%', p: 4, borderRadius: 1, textAlign: 'center' }}>
                    <LockResetIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                    <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
                        Password Reset Successful!
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Your password has been reset successfully. Redirecting to login...
                    </Typography>
                    <CircularProgress size={24} />
                </Paper>
            </Box>
        );
    }

    return (
        <Box sx={{ 
            minHeight: '100vh', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            // background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            p: 2
        }}>
            <Paper elevation={0} sx={{ maxWidth: 450, width: '100%', p: 4, borderRadius: 1 }}>
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <LockResetIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
                        Set New Password
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Please enter your new password below
                    </Typography>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}

                <form onSubmit={handleSubmit}>
                    <TextField
                        fullWidth
                        size='small'
                        type={showPassword ? 'text' : 'password'}
                        label="New Password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        disabled={loading}
                        required
                        sx={{ mb: 2 }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        helperText="Password must be at least 6 characters"
                    />

                    <TextField
                        fullWidth
                        size='small'
                        type="password"
                        label="Confirm Password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        disabled={loading}
                        required
                        sx={{ mb: 3 }}
                    />

                    <Button
                        fullWidth
                        type="submit"
                        variant="contained"
                        disabled={loading}
                        sx={{ py: 1.2, textTransform: 'none' }}
                    >
                        {loading ? <CircularProgress size={24} /> : 'Reset Password'}
                    </Button>

                    <Button
                        fullWidth
                        variant="text"
                        onClick={() => navigate('/login')}
                        disabled={loading}
                        sx={{ mt: 2, textTransform: 'none' }}
                    >
                        Back to Login
                    </Button>
                </form>
            </Paper>
        </Box>
    );
};

export default ResetPasswordPage;